import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { IDENTIFIED_BY_LABEL } from '@aeon-karaoke/cloud-convex/model/identify';
import { TRACK_STATE_LABEL, trackState } from '@aeon-karaoke/cloud-convex/model/trackState';
import { Button, LoadingState, MonoLabel, StripePlaceholder, Toggle } from '@/component';
import { useCurrentUser, useFavourites, useSongMatch, useTrack, useTrackAssets } from '@/hook';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { formatDuration } from '@/lib/time';
import { CorrectSongModal } from './CorrectSongModal';
import { TrackLyricsPanel } from './TrackLyricsPanel';

const KICKER_TONE = { ready: 'up', preparing: 'acc', attention: 'down' } as const;

const formatAdded = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/** /tracks/:trackId, the consumer track detail. Queue on an Aeon waits for a
 * live session (the mobile app owns that today); the owner gets the song
 * correction, the lyrics fixes, the session-sharing toggle and delete. */
export function TrackPage({ trackId }: { trackId: Id<'tracks'> }) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  // Once deleted the live read would throw Forbidden before the redirect lands,
  // so the subscription is dropped first.
  const [gone, setGone] = useState(false);
  const track = useTrack(gone ? undefined : trackId);
  const assets = useTrackAssets(gone ? undefined : trackId);
  const match = useSongMatch(gone ? undefined : trackId);
  const { favourites, toggle: toggleFavourite } = useFavourites();
  const [correcting, setCorrecting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (track === undefined) return <LoadingState label="Loading track…" />;

  const state = trackState(track.processingStatus);
  const owned = user?._id === track.ownerUserId;
  const ownUpload = owned && track.provenance === 'user';
  const songName = [match?.artist ?? track.artist, match?.title ?? track.title].filter(Boolean).join(' · ');

  const meta: Array<[string, ReactNode]> = [
    ['Artist', track.artist ?? '—'],
    ['Duration', track.durationMs !== undefined ? formatDuration(track.durationMs) : '—'],
    ['Source', ownUpload ? 'Your upload' : 'Aeon'],
    ['Added', formatAdded(track._creationTime)],
    [
      'Matched',
      match ? (
        <span className="inline-flex items-baseline gap-[10px]">
          <span>{[match.artist, match.title].filter(Boolean).join(' · ')}</span>
          <MonoLabel tone="mute">{IDENTIFIED_BY_LABEL[match.identifiedBy]}</MonoLabel>
          {owned && (
            <button type="button" onClick={() => setCorrecting(true)} className="text-[13px] text-acc hover:underline">
              Not this song?
            </button>
          )}
        </span>
      ) : (
        'Not yet'
      ),
    ],
  ];

  async function setShare(allow: boolean) {
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.tracks.setAllowSessionQueue, { trackId, allow }));
    setBusy(false);
    if (!result.ok) setError(result.error.message);
  }

  async function remove() {
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.tracks.remove, { trackId }));
    setBusy(false);
    if (!result.ok) return setError(result.error.message);
    setGone(true);
    void navigate({ to: '/library/mine', replace: true });
  }

  return (
    <div className="pt-10">
      <Link to="/library" className="hover:text-ink">
        <MonoLabel tone="mute">← Library</MonoLabel>
      </Link>
      <div className="mt-[26px] grid items-start gap-10 md:grid-cols-[220px_minmax(0,1fr)]">
        {assets?.coverUrl ? (
          <img src={assets.coverUrl} alt="" className="aspect-square w-full rounded-lg border border-line object-cover" />
        ) : (
          <StripePlaceholder caption="cover art" className="aspect-square rounded-lg border border-line" />
        )}
        <div>
          <MonoLabel tone={KICKER_TONE[state]} size="md" className="block">
            {TRACK_STATE_LABEL[state]}
          </MonoLabel>
          <h1 className="mt-3 text-[44px] font-semibold leading-[1.02] tracking-[-0.035em]">{track.title}</h1>
          {track.artist && <p className="mt-[10px] text-lg text-mute">{track.artist}</p>}
          {owned && state !== 'ready' && (
            <p className="mt-3 text-sm text-mute">
              {state === 'attention' ? 'This track could not be finished. ' : 'This track is still being built. '}
              <Link to="/create" search={{ track: trackId }} className="text-acc hover:underline">
                {state === 'attention' ? 'Fix it' : 'See progress'}
              </Link>
            </p>
          )}
          <div className="mt-[26px] flex flex-wrap gap-[10px]">
            <Button disabled title="Start a session on an Aeon system to queue this" className="px-[19px]">
              Queue on an Aeon
            </Button>
            <Button
              variant="outline"
              aria-pressed={favourites.has(trackId)}
              onClick={() => toggleFavourite(trackId)}
              className="px-[17px]"
            >
              {favourites.has(trackId) ? 'Favourited' : 'Favourite'}
            </Button>
            <Button variant="outline" quiet disabled title="Sharing arrives with friends" className="px-[17px]">
              Share
            </Button>
          </div>
          <dl className="mt-10">
            {meta.map(([key, value]) => (
              <div key={key} className="flex items-baseline justify-between gap-5 border-b border-stripeA py-[13px]">
                <dt>
                  <MonoLabel>{key}</MonoLabel>
                </dt>
                <dd className="text-right text-sm font-medium">{value}</dd>
              </div>
            ))}
          </dl>
          <TrackLyricsPanel track={track} owned={owned} song={songName} />
          {correcting && <CorrectSongModal trackId={trackId} match={match ?? null} onClose={() => setCorrecting(false)} />}
          {ownUpload && (
            <>
              <div className="mt-8 flex items-start justify-between gap-6 border-t border-line pt-5">
                <div>
                  <div className="text-[15px] font-semibold">Let friends queue this in sessions</div>
                  <p className="mt-1 max-w-[44ch] text-sm leading-[1.5] text-mute">
                    Anyone in a session with you can add it to the queue.
                  </p>
                </div>
                <Toggle
                  checked={track.allowSessionQueue ?? false}
                  onChange={(allow) => void setShare(allow)}
                  label="Let friends queue this in sessions"
                />
              </div>
              <div className="mt-7 flex items-center gap-4">
                {confirmDelete ? (
                  <>
                    <button type="button" onClick={() => void remove()} disabled={busy} className="text-[13px] font-semibold text-down">
                      Delete for good
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="text-[13px] text-mute">
                      Keep it
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => setConfirmDelete(true)} className="text-[13px] text-down">
                    Delete this track
                  </button>
                )}
              </div>
              {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
